/**
 * @fileoverview Syndicator Showdown — Multi-Runner Race Card
 * @module components/race/RaceCard
 */

import React, { useMemo, useCallback, useState } from 'react';
import { RunnerRow }     from './RunnerRow';
import { BuyModal }      from '../BuyModal';
import { useEntryState } from '../../context/EntryContext';
import { runAutoFillForRace, AUTO_FILL_STRATEGIES } from '../../utils/quickPick';
import { resolveGamePrizePool } from '../../utils/prizePool';
import { calculateMaxPerRace, calculateFieldPrices } from '@ss/shared';
import { MAX_FIELD_COVERAGE } from '@ss/shared/constants';
import './RaceCard.css';

const MEETING_COLORS = {
  NSW: 'var(--meeting-nsw)',
  VIC: 'var(--meeting-vic)',
  QLD: 'var(--meeting-qld)',
  WA:  'var(--meeting-wa)',
  SA:  'var(--meeting-sa)',
};

// Normalise any timestamp representation (ISO string, plain ms number, or
// Firestore Timestamp object) into a plain millisecond integer.
function toMs(v) {
  if (!v) return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return new Date(v).getTime();
  if (typeof v?.toMillis === 'function') return v.toMillis();
  return null;
}

export const RaceCard = ({
  race,
  onSell,
  isEntryLocked        = false,
  entryCloseMode       = 'first_race',
  firstRaceJump        = null,
  avgSpendPerRace      = 100_000,
  kMultiplier          = 2.900,
  hasPendingAllocation = false,
  onResolveAlert,
  slateData            = null,
  allRaceIds           = [],
}) => {
  const raceId = race.id;
  const { raceNumber, raceName, distance, classType, raceClass } = race;
  // Guard: runners can be null (Firestore null ≠ undefined, so default= [] won't fire)
  const runners = Array.isArray(race.runners) ? race.runners : [];
  const displayClass = classType || raceClass || '';

  // View control states
  const [buyModal, setBuyModal] = useState({ isOpen: false, runner: null, priceData: null });
  const [sortBy, setSortBy] = useState('number'); // 'number' | 'price'
  const [autoFillBusy, setAutoFillBusy] = useState(null);

  const {
    allocations,
    complianceSummary,
    financialAudit,
    perRaceFloor,
    totalRaces,
    totalStartingCapital,
    applyGeneratedStable,
    updateRunnerEquity,
  } = useEntryState();

  const accentColor = useMemo(
    () => MEETING_COLORS[race.meetingState?.toUpperCase()] || 'var(--accent-blue)',
    [race.meetingState]
  );

  const raceAlloc   = allocations[raceId]          || { runners: {}, totalSpentOnRace: 0 };
  const raceMetrics = complianceSummary[raceId]    || { isCompliant: false, floorShortfall: perRaceFloor };
  const currentRaceSpend = raceAlloc.totalSpentOnRace || 0;

  // Count non-scratched runners — mirrors SelectionPage's racesMetadata logic so
  // the buy-time field cap and the submission compliance check always agree.
  // Runners with null/undefined status are treated as active (scraper only marks 'Scratched').
  const activeRunnerCount = useMemo(
    () => runners.filter(r => {
      if (r.scratched) return false;
      const s = r.status?.toLowerCase?.();
      return !s?.includes('scratch');
    }).length,
    [runners]
  );

  const pricingMap = useMemo(() => {
    // calculateFieldPrices returns Array<{ id, baseSharePrice, maxOwnershipPct }>
    // avgSpendPerRace and kMultiplier come from slateData via SelectionPanel.
    const prices = calculateFieldPrices(runners, avgSpendPerRace, kMultiplier);
    const map = {};
    (Array.isArray(prices) ? prices : []).forEach(p => { map[p.id] = p; });
    return map;
  }, [runners, avgSpendPerRace, kMultiplier]);

  // ── Sorted Runners Array (Scratched pinned to bottom) ──────────────────────────
  // Number sort: ascending, missing numbers pushed to the end (not the front).
  // Price sort: descending (highest share price first), missing/non-positive
  // prices pushed to the end, tie-broken by runner number ascending. Sort is
  // deterministic and stable across re-renders/purchases since it only reads
  // runnerNumber + the memoised pricingMap, never allocation state.
  const sortedRunners = useMemo(() => {
    const active = runners.filter(r => r.status?.toLowerCase() !== 'scratched');
    const scratched = runners.filter(r => r.status?.toLowerCase() === 'scratched');

    const numberKey = (r) => Number.isFinite(r.runnerNumber) ? r.runnerNumber : Infinity;
    const priceKey  = (r) => {
      const p = pricingMap[r.id]?.baseSharePrice;
      return (typeof p === 'number' && p > 0) ? p : -Infinity;
    };

    const sorted = [...active].sort((a, b) => {
      if (sortBy === 'price') {
        const priceDiff = priceKey(b) - priceKey(a); // descending — highest first
        if (priceDiff !== 0) return priceDiff;
        return numberKey(a) - numberKey(b);          // tie-break: number ascending
      }
      return numberKey(a) - numberKey(b);             // ascending — lowest number first
    });

    return [...sorted, ...scratched];
  }, [runners, sortBy, pricingMap]);

  const maxAllowedBudget = useMemo(
    () => calculateMaxPerRace(totalStartingCapital, totalRaces || 1, perRaceFloor),
    [totalStartingCapital, totalRaces, perRaceFloor]
  );

  // Resolved GAME-economy prize pool for "Projected Payouts" — same priority
  // chain used by settleRaceResult.js and the value engine, so the preview
  // shown here never disagrees with an actual settlement.
  const gamePrizePool = useMemo(
    () => resolveGamePrizePool(race, slateData || {}),
    [race, slateData]
  );

  // Sum of floor shortfalls across every OTHER race on the slate — feeds
  // BuyModal's "Max Safe" quick-buy button. Races with no allocation yet
  // count as a full-floor shortfall.
  const reservedForFloors = useMemo(() => {
    let reserved = 0;
    for (const otherId of allRaceIds) {
      if (otherId === raceId) continue;
      const spent = allocations[otherId]?.totalSpentOnRace || 0;
      reserved += Math.max(0, perRaceFloor - spent);
    }
    return reserved;
  }, [allRaceIds, raceId, allocations, perRaceFloor]);

  // Unified race-lock check shared by RunnerRow (lock icon), BuyModal, and SellEquityModal.
  // Priority: admin-set status/lockedAt flag, then time-based check.
  //   first_race  → all races lock at the earliest jump across the entire slate
  //                 (firstRaceJump — the minimum startTimestamp, computed in SelectionPanel)
  //   per_race    → each race locks independently at its own startTimestamp (preferred)
  //                 or scheduledJump (fallback if startTimestamp absent on the race subdoc)
  const isRaceLocked = useMemo(() => {
    if (race.status === 'locked' || race.lockedAt != null) return true;
    const jumpMs = entryCloseMode === 'first_race'
      ? toMs(firstRaceJump)
      : toMs(race.startTimestamp ?? race.scheduledJump);
    return jumpMs != null && Date.now() >= jumpMs;
  }, [race.status, race.lockedAt, race.startTimestamp, race.scheduledJump, entryCloseMode, firstRaceJump]);

  // Per-race "Auto-Fill This Leg" — Balanced / Value / Lucky Dip. Uses the
  // SAME buildRaceContext + validateAndTopUp engine as whole-slate Quick
  // Pick (utils/quickPick.js), scoped to just this race, so it respects the
  // 100% cap, the 50% field-coverage cap, the race floor, and the wallet —
  // and only ever replaces this race's allocation, never any other race's.
  const handleLocalAutoGenerate = useCallback((strategy) => {
    if (autoFillBusy) return;

    const alreadyHasSelections = (raceAlloc.runnersPicked || 0) > 0;
    if (alreadyHasSelections) {
      const ok = window.confirm(
        `This will replace your current selections in Leg ${raceNumber}${raceName ? ` (${raceName})` : ''}. Continue?`
      );
      if (!ok) return;
    }

    setAutoFillBusy(strategy);
    try {
      // Replacing this race frees up whatever it currently has spent, so the
      // real budget ceiling for the NEW allocation is remaining wallet + that.
      const budgetCeiling = (financialAudit.remainingWallet || 0) + currentRaceSpend;

      const result = runAutoFillForRace({
        race, avgSpendPerRace, kMultiplier, perRaceFloor, strategy, budgetCeiling,
      });

      if (result.aborted) {
        window.alert(result.message || 'Could not auto-fill this race. Please select runners manually.');
        return;
      }

      applyGeneratedStable({ ...allocations, [raceId]: result.allocation });
    } finally {
      setAutoFillBusy(null);
    }
  }, [
    autoFillBusy, raceAlloc.runnersPicked, raceNumber, raceName, race, avgSpendPerRace,
    kMultiplier, perRaceFloor, financialAudit.remainingWallet, currentRaceSpend,
    allocations, applyGeneratedStable, raceId,
  ]);

  return (
    <div className="ss-race-card">
      <div className="ss-card-accent-strip" style={{ backgroundColor: accentColor }} />

      <div className="ss-card-header">
        <div className="ss-header-meta">
          <span className="ss-race-index-badge">LEG {raceNumber}</span>
          <div className="ss-race-title-group">
            <h3 className="ss-race-name-heading">{raceName || `Race Leg ${raceNumber}`}</h3>
            <p className="ss-race-specs-subtext">
              {distance ? `${distance}m` : ''}{displayClass ? ` · ${displayClass}` : ''} · {activeRunnerCount} runners
            </p>
          </div>
        </div>
        <div className="ss-header-compliance-stamp">
          {raceMetrics.isCompliant ? (
            <span className="ss-pill-badge ss-pill-badge--success">✓ COMPLIANT</span>
          ) : (
            <span className="ss-pill-badge ss-pill-badge--warning">⚠ INCOMPLETE</span>
          )}
        </div>
      </div>

      {(() => {
        const runnersHeld       = raceAlloc.runnersPicked || 0;
        const maxRunnersAllowed = Math.max(1, Math.floor(activeRunnerCount * MAX_FIELD_COVERAGE));
        const atRunnerCap       = runnersHeld >= maxRunnersAllowed;
        return (
          <div className="ss-budget-dashboard-strip">
            <div className="ss-budget-metric">
              <span className="ss-dashboard-label">Spent Here</span>
              <span className="ss-dashboard-value" style={{ color: 'var(--accent-blue)' }}>
                ${currentRaceSpend.toLocaleString('en-AU', { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="ss-budget-metric">
              <span className="ss-dashboard-label">Floor Shortfall</span>
              <span className="ss-dashboard-value" style={{ color: raceMetrics.floorShortfall > 0 ? 'var(--accent-amber)' : 'var(--accent-green)' }}>
                {raceMetrics.floorShortfall > 0 ? `$${Math.round(raceMetrics.floorShortfall).toLocaleString('en-AU')}` : 'Cleared'}
              </span>
            </div>
            <div className="ss-budget-metric">
              <span className="ss-dashboard-label">Runners</span>
              <span className="ss-dashboard-value" style={{ color: atRunnerCap ? 'var(--accent-red)' : 'var(--text-secondary)' }}>
                {runnersHeld} / {maxRunnersAllowed} max
              </span>
            </div>
            <div className="ss-budget-metric ss-budget-metric--right">
              <span className="ss-dashboard-label">Wallet Left</span>
              <span className="ss-dashboard-value" style={{ color: 'var(--text-primary)' }}>
                ${Math.round(financialAudit.remainingWallet).toLocaleString('en-AU')}
              </span>
            </div>
          </div>
        );
      })()}

      <div className="ss-local-generator-bar">
        <span className="ss-gen-prompt-text">Auto-Fill This Leg:</span>
        <div className="ss-gen-button-group">
          <button
            onClick={() => handleLocalAutoGenerate(AUTO_FILL_STRATEGIES.BALANCED)}
            className="ss-gen-btn"
            disabled={!!autoFillBusy}
            type="button"
          >
            {autoFillBusy === AUTO_FILL_STRATEGIES.BALANCED ? '⟳' : 'Balanced'}
          </button>
          <button
            onClick={() => handleLocalAutoGenerate(AUTO_FILL_STRATEGIES.VALUE)}
            className="ss-gen-btn ss-gen-btn--value"
            disabled={!!autoFillBusy}
            type="button"
          >
            {autoFillBusy === AUTO_FILL_STRATEGIES.VALUE ? '⟳' : 'Value'}
          </button>
          <button
            onClick={() => handleLocalAutoGenerate(AUTO_FILL_STRATEGIES.LUCKY_DIP)}
            className="ss-gen-btn ss-gen-btn--lucky"
            disabled={!!autoFillBusy}
            type="button"
          >
            {autoFillBusy === AUTO_FILL_STRATEGIES.LUCKY_DIP ? '⟳' : 'Lucky Dip'}
          </button>
        </div>
      </div>

      {/* Sort controls bar */}
      <div className="ss-card-sort-bar">
        <span className="ss-card-sort-label">Sort:</span>
        <button
          className={`ss-card-sort-btn ${sortBy === 'number' ? 'ss-card-sort-btn--active' : ''}`}
          onClick={() => setSortBy('number')}
          aria-pressed={sortBy === 'number'}
          type="button"
        >
          # Number
        </button>
        <button
          className={`ss-card-sort-btn ${sortBy === 'price' ? 'ss-card-sort-btn--active' : ''}`}
          onClick={() => setSortBy('price')}
          aria-pressed={sortBy === 'price'}
          type="button"
        >
          $ Price
        </button>
      </div>

      <div className="ss-card-runners-list">
        {sortedRunners.map(runnerItem => {
          const priceData     = pricingMap[runnerItem.id];
          const currentEquity = raceAlloc.runners?.[runnerItem.id]?.equityPercent ?? 0;
          const currentCost   = raceAlloc.runners?.[runnerItem.id]?.purchaseCost  ?? 0;
          return (
            <RunnerRow
              key={runnerItem.id}
              runner={runnerItem}
              baseSharePrice={priceData?.baseSharePrice  ?? 0}
              maxOwnershipPct={priceData?.maxOwnershipPct ?? 100}
              currentEquity={currentEquity}
              isRaceLocked={isRaceLocked}
              isEntryLocked={isEntryLocked}
              hasPendingAllocation={hasPendingAllocation}
              onBuy={() => setBuyModal({ isOpen: true, runner: runnerItem, priceData })}
              onSell={onSell && currentEquity > 0
                ? () => onSell({
                    runnerId:     runnerItem.id,
                    runnerName:   runnerItem.name || runnerItem.id,
                    currentEquity,
                    currentCost,
                    raceId,
                    isRaceLocked,
                  })
                : undefined
              }
            />
          );
        })}
      </div>

      <BuyModal
        isOpen={buyModal.isOpen}
        onClose={() => setBuyModal({ isOpen: false, runner: null, priceData: null })}
        onConfirm={(equity, totalCost) => {
          updateRunnerEquity(raceId, buyModal.runner.id, equity, totalCost);
          // If the player is buying as part of a scratch refund allocation, mark it resolved
          if (hasPendingAllocation && onResolveAlert) {
            onResolveAlert(raceId);
          }
          setBuyModal({ isOpen: false, runner: null, priceData: null });
        }}
        runner={buyModal.runner}
        raceId={raceId}
        raceNumber={raceNumber}
        raceName={raceName}
        baseSharePrice={buyModal.priceData?.baseSharePrice  ?? 0}
        maxOwnershipPct={buyModal.priceData?.maxOwnershipPct ?? 100}
        currentEquity={raceAlloc.runners?.[buyModal.runner?.id]?.equityPercent ?? 0}
        currentRaceSpend={currentRaceSpend}
        avgSpendPerRace={avgSpendPerRace}
        remainingBudget={financialAudit.remainingWallet}
        reservedForFloors={reservedForFloors}
        perRaceFloor={perRaceFloor}
        runnersCurrentlyHeld={raceAlloc.runnersPicked || 0}
        activeRunnerCount={activeRunnerCount}
        gamePrizePool={gamePrizePool}
        isRaceLocked={isRaceLocked}
      />
    </div>
  );
};

export default RaceCard;